import { FC } from "react";

import { Stack } from "@mui/material";

import { TableCellContainer } from "./TableCellContainer";
import { TableCellChip } from "./types";

import { DashboardChip } from "@/dashboard-design-system/DashboardChip/DashboardChip";
import { TruncatedTextWithTooltip } from "@/dashboard-design-system/DashboardTableCustomization/TruncatedTextWithTooltip/TruncatedTextWithTooltip";
import { DashboardTooltip } from "@/dashboard-design-system/DashboardTooltip/DashboardTooltip";

const CHIP_MAX_WIDTH = 160;

const truncatedChipSx = { maxWidth: CHIP_MAX_WIDTH, minWidth: 0 };
const truncatedChipLabelSx = { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };
const getTruncatedChipLabel = (label: string) => (
  <TruncatedTextWithTooltip tooltipProps={{ title: label, placement: "top" }}>{label}</TruncatedTextWithTooltip>
);

interface TableSingleSelectCellProps {
  label: string;
}

export const TableSingleSelectCell: FC<TableSingleSelectCellProps> = ({ label }) => (
  <TableCellContainer layout="inline" data-test="table-single-select-cell">
    <DashboardChip
      variant="filled"
      shape="rounded"
      vibe="neutral"
      label={getTruncatedChipLabel(label)}
      sx={truncatedChipSx}
      slotProps={{ label: { sx: truncatedChipLabelSx } }}
    />
  </TableCellContainer>
);

interface TableMultiSelectCellProps {
  labels: string[];
  visibleCount?: number;
}

const DEFAULT_VISIBLE_CHIPS = 3;

export const TableMultiSelectCell: FC<TableMultiSelectCellProps> = ({
  labels,
  visibleCount = DEFAULT_VISIBLE_CHIPS,
}) => {
  const visibleLabels = labels.slice(0, visibleCount);
  const hiddenLabels = labels.slice(visibleCount);

  return (
    <TableCellContainer layout="inline" data-test="table-multi-select-cell">
      {visibleLabels.map((label) => (
        <DashboardChip
          key={label}
          variant="filled"
          shape="rounded"
          vibe="neutral"
          label={getTruncatedChipLabel(label)}
          sx={truncatedChipSx}
          slotProps={{ label: { sx: truncatedChipLabelSx } }}
        />
      ))}
      {hiddenLabels.length > 0 && (
        <DashboardTooltip title={hiddenLabels.join(", ")} placement="top">
          <span>
            <DashboardChip variant="filled" shape="rounded" vibe="neutral" label={`+${hiddenLabels.length}`} />
          </span>
        </DashboardTooltip>
      )}
    </TableCellContainer>
  );
};

interface TableCheckboxCellProps {
  isChecked: boolean;
  yesLabel: string;
  noLabel: string;
}

export const TableCheckboxCell: FC<TableCheckboxCellProps> = ({ isChecked, yesLabel, noLabel }) => (
  <TableCellContainer layout="centered" data-test="table-checkbox-cell">
    <DashboardChip
      variant="filled"
      shape="rounded"
      vibe={isChecked ? "positive" : "danger"}
      label={isChecked ? yesLabel : noLabel}
    />
  </TableCellContainer>
);

interface TableChipListCellProps {
  chips: TableCellChip[];
}

export const TableChipListCell: FC<TableChipListCellProps> = ({ chips }) => (
  <TableCellContainer layout="stacked" gap={1} data-test="table-chip-list-cell">
    <Stack gap={1} alignItems="flex-start" minWidth={0}>
      {chips.map((chip) => (
        <DashboardChip
          key={chip.label}
          variant="outlined"
          shape="pill"
          vibe={chip.vibe ?? "neutral"}
          label={chip.label}
          sx={truncatedChipSx}
          slotProps={{ label: { sx: truncatedChipLabelSx } }}
        />
      ))}
    </Stack>
  </TableCellContainer>
);
